// ── the private ring · what the person sees ──────────────────────────────────
//
// The shapes GET /api/you/ring answers with, and the pure grouping behind
// "who has seen what". Client-safe: no I/O, no crypto, no server imports.

import type { Depth, GrantLength, LedgerDecision, Tier, WriteMode } from './ring'
import { categoryWords, normCategories, type Category } from './categories'

export interface RingApp {
  kind: 'connection' | 'api_key'
  id: string
  /** the person's name for it */
  label: string
  /** a connection's catalogue tool ('claude-code', 'chatgpt', …) */
  toolId: string | null
  /** an api key's visible prefix */
  keyPrefix: string | null
  depth: Depth
  askFirst: boolean
  /** what it may write (149): free before, and until 149 is on the database */
  writeMode: WriteMode
  lastSeenAt: string | null
}

export interface RingRequest {
  id: string
  app: { kind: RingApp['kind']; id: string; label: string }
  client: string | null
  tool: string
  /** the app's ask, opened for its owner (null when it would not open) */
  ask: string | null
  factCount: number
  depthNeeded: Exclude<Depth, 'standard'>
  /** what the held facts are about (149), for the person only; null when unknown */
  categories: Category[] | null
  askedAt: string
  asks: number
}

/** A grant an app holds now: a window that ends, or a standing one narrowed to categories. */
export interface RingWindow {
  requestId: string
  app: { kind: RingApp['kind']; id: string; label: string }
  /** when it ends; null = until the person ends it (always, narrowed) */
  until: string | null
  depth: Exclude<Depth, 'standard'>
  /** null = the whole depth */
  categories: Category[] | null
  /** where it was given: an answer to a request, or settings */
  from: 'request' | 'settings'
}

/** What the person sees of a held write: its words as the app sent them, clipped. */
export interface ProposalPreview {
  /** the note, the content, or what would be forgotten (≤ 600 characters) */
  text: string | null
  /** the person's own words, when the app passed them (user_words) */
  said: string | null
  /** the pages a save would write, by name */
  pages: string[]
}

/** A write an app that asks first sent, waiting on the person (or answered this week). */
export interface ProposalView {
  id: string
  app: { kind: RingApp['kind'] | 'gone'; id: string | null; label: string }
  client: string | null
  tool: string
  kind: 'remember' | 'save' | 'forget'
  project: string | null
  /** null once decided (its words are gone), or when they would not open */
  preview: ProposalPreview | null
  size: number
  createdAt: string
  status: 'pending' | 'kept' | 'dropped' | 'expired' | 'failed'
  decidedAt: string | null
  outcome: string | null
}

/** One read receipt as the ring shows it: names and counts, never a word of what was read. */
export interface ReceiptView {
  tool: string
  via: 'mcp' | 'rest'
  projects: string[]
  pages: string[]
  categories: Category[]
  pageCount: number
  factCount: number
  withheld: number
  chars: number
  at: string
}

export interface RingSeen {
  factId: string | null
  disclosure: Extract<Tier, 'self' | 'private'>
  dimension: string | null
  /** a self fact's words; a private fact's are never sent to this surface */
  statement: string | null
  tool: string
  ask: string | null
  decision: LedgerDecision
  at: string
}

export interface RingHistoryGroup {
  app: { kind: RingApp['kind'] | 'gone'; id: string | null; label: string }
  client: string | null
  /** distinct facts this app has been sent */
  facts: number
  /** responses that carried at least one */
  times: number
  lastAt: string
  seen: RingSeen[]
}

export interface RingPayload {
  /** false while migration 128 is not on the database */
  ready: boolean
  sealing: {
    /** a content key exists on this deployment */
    on: boolean
    /** this person's current private / self facts whose words are not sealed yet */
    unsealed: { private: number; self: number }
  }
  apps: RingApp[]
  requests: RingRequest[]
  windows: RingWindow[]
  history: RingHistoryGroup[]
  /** false while migration 149 is not on the database: grants are 128's, writes are free, no receipts */
  passes: boolean
  /** writes waiting on the person, and what became of this week's (149) */
  proposals: ProposalView[]
  /** each app's recent read receipts, newest first, by "<kind>:<id>" (149) */
  receipts: Record<string, ReceiptView[]>
}

/** One reveal row, joined to what the page may say about its fact. */
export interface RevealRowLike {
  fact_id: string | null
  connection_id: string | null
  api_key_id: string | null
  client: string | null
  tool: string
  disclosure: string
  decision: string
  revealed_at: string
  ask: string | null
}

/**
 * Group the reveal ledger by app, newest first. `appLabel` names an app by its
 * id (null when it has been let go since); `fact` says what may be shown of a
 * fact (null once it is forgotten). Within an app, a fact appears once, at the
 * newest time it was sent.
 */
export function groupHistory(
  rows: RevealRowLike[],
  appLabel: (kind: 'connection' | 'api_key', id: string) => string | null,
  fact: (id: string) => { dimension: string | null; statement: string | null } | null,
): RingHistoryGroup[] {
  const groups = new Map<string, RingHistoryGroup & { _seen: Map<string, RingSeen>; _times: Set<string> }>()
  const sorted = [...rows].sort((a, b) => b.revealed_at.localeCompare(a.revealed_at))
  for (const r of sorted) {
    const kind = r.connection_id ? 'connection' : r.api_key_id ? 'api_key' : null
    const id = r.connection_id ?? r.api_key_id ?? null
    const key = kind && id ? `${kind}:${id}` : `gone:${r.client ?? ''}`
    let g = groups.get(key)
    if (!g) {
      const label = kind && id ? appLabel(kind, id) : null
      g = {
        app: kind && id && label ? { kind, id, label } : { kind: 'gone', id: null, label: label ?? r.client ?? 'an app you let go' },
        client: r.client,
        facts: 0,
        times: 0,
        lastAt: r.revealed_at,
        seen: [],
        _seen: new Map(),
        _times: new Set(),
      }
      groups.set(key, g)
    }
    // one response = one tool call at one instant
    g._times.add(`${r.tool}@${r.revealed_at.slice(0, 19)}`)
    const fk = r.fact_id ?? `forgotten:${r.revealed_at}`
    if (!g._seen.has(fk)) {
      const f = r.fact_id ? fact(r.fact_id) : null
      g._seen.set(fk, {
        factId: r.fact_id,
        disclosure: r.disclosure === 'private' ? 'private' : 'self',
        dimension: f?.dimension ?? null,
        statement: r.disclosure === 'private' ? null : (f?.statement ?? null),
        tool: r.tool,
        ask: r.ask,
        decision: r.decision === 'once' || r.decision === 'approved' || r.decision === 'abstracted' ? r.decision : 'allowed',
        at: r.revealed_at,
      })
    }
  }
  return [...groups.values()].map(({ _seen, _times, ...g }) => ({
    ...g,
    facts: _seen.size,
    times: _times.size,
    seen: [..._seen.values()],
  }))
}

/** The words for a depth, as the controls say them. */
export const DEPTH_WORDS: Record<Depth, { label: string; says: string }> = {
  standard: { label: 'standard', says: 'trusted, work and public facts' },
  personal: { label: 'personal', says: 'also the facts you keep to yourself' },
  private: { label: 'private', says: 'also your private facts' },
}

/** How a fact was cleared, in words. */
export const DECISION_WORDS: Record<LedgerDecision, string> = {
  allowed: 'you set this app to read them',
  once: 'you allowed it for a while',
  approved: 'you allowed it always',
  abstracted: 'its words stayed here; netti only shaped a hint from it',
}

// ── requests waiting on the person (the inbox and the you page read these) ──

/** A pending request goes quiet after this long (the door expires it on the next ask). */
export const ASK_STALE_MS = 7 * 86_400_000

/** One self_access_requests row, its ask already opened for its owner. */
export interface AccessRowLike {
  id: string
  connection_id: string | null
  api_key_id: string | null
  client: string | null
  tool: string | null
  /** the ask, opened (null when it would not open, or there was none) */
  ask: string | null
  fact_count: number | string | null
  depth_needed: string | null
  status: string
  asked_at: string
  asks: number | string | null
  window_until: string | null
  /** 149: what a pending request is about, or what a grant covers */
  categories?: unknown
  /** 149: 'settings' for a grant made from settings */
  decided_at?: string | null
}

/** What waits on the person: requests to answer, the grants still open, and (149) the writes held for a yes. */
export interface RingAsks {
  requests: RingRequest[]
  windows: RingWindow[]
  /** per app, how many of its writes wait on the person (149); answered in the ring */
  held?: RingHeld[]
  /** 149 is on: answers can carry a length and what they cover */
  passes?: boolean
}

/** How many writes one app left waiting on the person. */
export interface RingHeld {
  app: { kind: RingApp['kind']; id: string; label: string }
  count: number
}

export const NO_ASKS: RingAsks = Object.freeze({ requests: [], windows: [] }) as RingAsks

/** Count pending held writes per app (rows of self_write_proposals: connection_id / api_key_id), most first. */
export function heldByApp(
  rows: ReadonlyArray<Record<string, unknown>>,
  appLabel: (kind: 'connection' | 'api_key', id: string) => string | null,
): RingHeld[] {
  const counts = new Map<string, RingHeld>()
  for (const r of rows) {
    const kind = r.connection_id ? ('connection' as const) : r.api_key_id ? ('api_key' as const) : null
    const id = r.connection_id ? String(r.connection_id) : r.api_key_id ? String(r.api_key_id) : null
    if (!kind || !id) continue
    const key = `${kind}:${id}`
    const was = counts.get(key)
    if (was) was.count += 1
    else counts.set(key, { app: { kind, id, label: appLabel(kind, id) ?? 'an app' }, count: 1 })
  }
  return [...counts.values()].sort((a, b) => b.count - a.count)
}

/** "claude wants to write 3 things into your memory" */
export function heldHeadline(h: RingHeld): string {
  return `${h.app.label} wants to write ${h.count === 1 ? 'something' : `${h.count} things`} into your memory`
}

/**
 * Pending, unstale requests (newest first) and live windows, from the rows the
 * owner may read. `appLabel` names an app by its id; an app let go since is
 * named by its client, else plainly.
 */
export function shapeAccess(
  rows: readonly AccessRowLike[],
  now: number,
  appLabel: (kind: 'connection' | 'api_key', id: string) => string | null,
): RingAsks {
  const requests: RingRequest[] = []
  const windows: RingWindow[] = []
  const sorted = [...rows].sort((a, b) => String(b.asked_at).localeCompare(String(a.asked_at)))
  for (const r of sorted) {
    const kind = r.connection_id ? ('connection' as const) : r.api_key_id ? ('api_key' as const) : null
    const id = r.connection_id ?? r.api_key_id
    if (!kind || !id) continue
    const app = { kind, id, label: appLabel(kind, id) ?? r.client ?? 'an app you let go' }
    const depth = r.depth_needed === 'private' ? ('private' as const) : ('personal' as const)
    const categories = normCategories(r.categories)
    const from = r.tool === 'settings' ? ('settings' as const) : ('request' as const)
    if (r.status === 'pending') {
      const at = Date.parse(r.asked_at)
      if (!Number.isFinite(at) || now - at > ASK_STALE_MS) continue
      requests.push({
        id: r.id,
        app,
        client: r.client,
        tool: String(r.tool ?? ''),
        ask: r.ask,
        factCount: Math.max(0, Number(r.fact_count) || 0),
        depthNeeded: depth,
        categories,
        askedAt: r.asked_at,
        asks: Math.max(1, Number(r.asks) || 1),
      })
    } else if (r.status === 'allowed_once' && r.window_until && Date.parse(r.window_until) > now) {
      windows.push({ requestId: r.id, app, until: r.window_until, depth, categories, from })
    } else if (r.status === 'allowed_always' && categories && r.window_until == null) {
      // a standing grant narrowed to categories (149): it lasts until the person ends it
      windows.push({ requestId: r.id, app, until: null, depth, categories, from })
    }
  }
  return { requests, windows }
}

/** "3 private things", "1 personal thing": what a request is for, in the person's words. */
export function thingsWord(n: number, depth: Exclude<Depth, 'standard'>): string {
  return `${n} ${depth === 'private' ? 'private' : 'personal'} thing${n === 1 ? '' : 's'}`
}

/** "about how you work and what you like" (empty when it is about nothing we can name). */
export function aboutWords(categories: readonly Category[] | null | undefined): string {
  const w = categoryWords(categories)
  return w ? `about ${w}` : ''
}

/** The line a request wears: "claude asked to use 3 private things about your body and health". */
export function askHeadline(r: Pick<RingRequest, 'app' | 'factCount' | 'depthNeeded'> & { categories?: Category[] | null }): string {
  const about = aboutWords(r.categories)
  return `${r.app.label} asked to use ${thingsWord(r.factCount, r.depthNeeded)}${about ? ` ${about}` : ''}`
}

/** How long a grant lasts, as the choices say it. */
export const LENGTH_WORDS: Record<GrantLength, string> = {
  once: '30 minutes',
  day: 'a day',
  week: 'a week',
  always: 'always',
}

/** The same, as the end of a sentence: "for a day", "from now on". */
export const LENGTH_SAID: Record<GrantLength, string> = {
  once: 'for the next 30 minutes',
  day: 'for a day',
  week: 'for a week',
  always: 'from now on',
}

/** What happened once the person answered, said back in one line. */
export function decidedLine(
  decision: 'allowed_once' | 'allowed_always' | 'denied',
  r: Pick<RingRequest, 'app' | 'depthNeeded'> & { categories?: Category[] | null },
  length?: GrantLength,
): string {
  const kind = r.depthNeeded === 'private' ? 'private' : 'personal'
  const about = aboutWords(r.categories)
  const what = `your ${kind} things${about ? ` ${about}` : ''}`
  if (decision === 'denied') return `not shared. ${r.app.label} will not ask again today.`
  if (decision === 'allowed_always') return `${r.app.label} can use ${what} from now on${about ? '' : ', without asking'}.`
  return `${r.app.label} can use ${what} ${LENGTH_SAID[length ?? 'once']}.`
}

/** What is left of a grant: "for 12 more minutes" · "for 5 more hours" · "for 6 more days" · "until you end it". */
export function grantLeft(until: string | null, now: number): string {
  if (until == null) return 'until you end it'
  const ms = Date.parse(until) - now
  if (!Number.isFinite(ms) || ms <= 0) return 'for less than a minute'
  const m = Math.max(1, Math.ceil(ms / 60_000))
  if (m < 90) return `for ${m} more minute${m === 1 ? '' : 's'}`
  const h = Math.round(m / 60)
  if (h < 36) return `for ${h} more hour${h === 1 ? '' : 's'}`
  const d = Math.round(h / 24)
  return `for ${d} more day${d === 1 ? '' : 's'}`
}

/** A grant in one line: "personal things about how you work, for 6 more days". */
export function grantLine(w: Pick<RingWindow, 'depth' | 'categories' | 'until'>, now: number): string {
  const about = aboutWords(w.categories)
  return `${w.depth === 'private' ? 'private' : 'personal'} things${about ? ` ${about}` : ''}, ${grantLeft(w.until, now)}`
}

/** The write modes, as the control says them. */
export const WRITE_WORDS: Record<WriteMode, { label: string; says: string }> = {
  free: { label: 'writes freely', says: 'what it learns goes straight into your memory' },
  ask: { label: 'asks first', says: 'what it would write waits here for your yes' },
  read_only: { label: 'read only', says: 'it reads, and writes nothing' },
}

/** A held write in one line: "claude wants to remember something about you". */
export function proposalHeadline(p: Pick<ProposalView, 'app' | 'kind' | 'project'>): string {
  if (p.kind === 'forget') return `${p.app.label} wants to forget something`
  if (p.kind === 'save') return `${p.app.label} wants to save${p.project ? ` into ${p.project}` : ' into your memory'}`
  return `${p.app.label} wants to remember something about you`
}

/** A receipt in one line: "read 4 pages of innernet and 6 facts about how you work". */
export function receiptLine(r: Pick<ReceiptView, 'projects' | 'pageCount' | 'factCount' | 'categories' | 'withheld' | 'tool'>): string {
  const parts: string[] = []
  if (r.pageCount > 0) parts.push(`${r.pageCount} page${r.pageCount === 1 ? '' : 's'}${r.projects.length === 1 ? ` of ${r.projects[0]}` : r.projects.length > 1 ? ` across ${r.projects.length} projects` : ''}`)
  else if (r.projects.length) parts.push(r.projects.length === 1 ? r.projects[0] : `${r.projects.length} projects`)
  if (r.factCount > 0) {
    const about = aboutWords(r.categories)
    parts.push(`${r.factCount} fact${r.factCount === 1 ? '' : 's'} about you${about ? ` (${about.replace(/^about /, '')})` : ''}`)
  }
  const read = parts.length ? `read ${parts.join(' and ')}` : `read with ${r.tool.replace(/^innernet_/, '').replace(/_/g, ' ')}`
  return r.withheld > 0 ? `${read} · ${r.withheld} held back` : read
}

/** The receipts of each app, newest first, keyed "<kind>:<id>" (from read_receipts rows). */
export function groupReceipts(rows: ReadonlyArray<Record<string, unknown>>, cap = 30): Record<string, ReceiptView[]> {
  const out: Record<string, ReceiptView[]> = {}
  const sorted = [...rows].sort((a, b) => String(b.read_at ?? '').localeCompare(String(a.read_at ?? '')))
  for (const r of sorted) {
    const key = r.connection_id ? `connection:${r.connection_id}` : r.api_key_id ? `api_key:${r.api_key_id}` : null
    if (!key) continue
    const list = (out[key] ??= [])
    if (list.length >= cap) continue
    list.push({
      tool: String(r.tool ?? ''),
      via: r.via === 'rest' ? 'rest' : 'mcp',
      projects: Array.isArray(r.projects) ? r.projects.map(String) : [],
      pages: Array.isArray(r.pages) ? r.pages.map(String) : [],
      categories: normCategories(r.categories) ?? [],
      pageCount: Math.max(0, Number(r.page_count) || 0),
      factCount: Math.max(0, Number(r.fact_count) || 0),
      withheld: Math.max(0, Number(r.withheld) || 0),
      chars: Math.max(0, Number(r.chars) || 0),
      at: String(r.read_at),
    })
  }
  return out
}

/** When an app was last sent a self or private fact (null: never). */
export function lastRevealOf(history: readonly RingHistoryGroup[], app: Pick<RingApp, 'kind' | 'id'>): string | null {
  const g = history.find((h) => h.app.kind === app.kind && h.app.id === app.id)
  return g ? g.lastAt : null
}

/** An app as the controls describe it: its depth, whether it asks, what it reads. */
export function depthSummary(app: Pick<RingApp, 'depth' | 'askFirst'>): string {
  if (app.depth === 'standard') return 'standard'
  return `${app.depth} · ${app.askFirst ? 'asks first' : 'does not ask'}`
}

/** "just now" · "4 minutes ago" · "2 hours ago" · "3 days ago" (a clock handed in, never read here) */
export function agoWords(iso: string, now: number): string {
  const s = Math.max(0, Math.floor((now - Date.parse(iso)) / 1000))
  if (!Number.isFinite(s) || s < 60) return 'just now'
  const m = Math.floor(s / 60)
  if (m < 60) return `${m} minute${m === 1 ? '' : 's'} ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`
  const d = Math.floor(h / 24)
  return `${d} day${d === 1 ? '' : 's'} ago`
}

/** minutes left in an allow-once window, at least 1 while it is open */
export function minutesLeft(until: string, now: number): number {
  return Math.max(1, Math.ceil((Date.parse(until) - now) / 60_000))
}

/** "claude’s", "the notes’": a name's possessive, for "manage claude’s access" */
export function possessive(name: string): string {
  const n = name.trim()
  if (!n) return 'this app’s'
  return /s$/i.test(n) ? `${n}’` : `${n}’s`
}
